import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">&ldquo;{quote}&rdquo;</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

const faqs = [
  {
    question: 'Does Bedford\'s RED DfT rating mean I will automatically win a claim?',
    answer: 'No. RED means the Department for Transport judges Bedford\'s overall maintenance performance weak — particularly spend — but Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired. Your road class, prior reports, photos and inspection intervals matter more than the headline rating alone.',
  },
  {
    question: 'What if my pothole was on a residential or unclassified road?',
    answer: 'Unclassified roads make up 530km — 61% of Bedford\'s 874.1km network. The DfT scores U-road condition at 20.0/100 (RED) versus A-roads at 93.0/100. CVI surveys cover 100% of U-roads over a two-year north/south cycle. At the 2025 survey, 18% of U-roads were in RED condition — eleven times the A-road rate of 1.6%. If your incident fell between survey halves, network data may not reflect the defect on your street.',
  },
  {
    question: 'Why is the spend scorecard RED when Bedford often spends above its DfT allocation?',
    answer: 'The DfT spend scorecard compares capital investment against allocation and treatment outcomes — not raw pounds alone. Bedford exceeded allocation in 2020/21 through 2024/25, yet projected 2025/26 capital spend (£3.21m) sits below the £6.32m DfT allocation. Combined with U-road condition at 20/100 despite 62–70% preventative spend in recent years, the DfT awarded RED on spend.',
  },
  {
    question: 'U-road RED condition jumped to 38% in 2023 then fell to 16% — what does that mean?',
    answer: 'Bedford\'s transparency report publishes the percentage only — it does not explain year-on-year swings. RED condition on U-roads rose from 18% (2020) to 38% (2023), then dropped to 16% (2024) and returned to 18% (2025). The council does not state whether this reflects survey methodology, targeted works, or network recovery. For a claim, focus on the condition record for your road and any prior reports — not speculation about causes.',
  },
  {
    question: 'My damage was on an A-road or the A6 — does the U-road argument still apply?',
    answer: 'Less directly. A-roads score 93.0/100 with just 1.6% in RED condition at the 2025 survey — meeting Bedford\'s 2% target. The A6 Paula Radcliffe Way received additional funding the council cites as improving classified roads. Claims on well-maintained A-roads may face a stronger Section 58 defence unless you can show the specific defect was known, reported, or visible beyond inspection intervals. Trunk roads and motorways are maintained by National Highways, not Bedford Borough Council.',
  },
  {
    question: 'Pothole repairs rose from 1,343 to 6,308 — but the early figures count "orders raised". Does that matter?',
    answer: 'Yes. Bedford notes that 2020/21 and 2021/22 figures are the number of orders raised, not individual potholes. From late 2022 the council added a field to count potholes per street or order. The 2022/23–2024/25 figures (3,841 → 6,032 → 6,308) are directly comparable and show reactive workload roughly tripling in three years — evidence of ongoing defects, not proof your specific pothole was unavoidable.',
  },
  {
    question: 'How do I report a pothole to Bedford Borough Council?',
    answer: 'Report potholes via Bedford\'s online form at bedford.gov.uk — give precise location detail including landmarks or what3words where safe. The A421 is managed by National Highways (0300 123 5000). Prior reports of the same defect strengthen a claim by demonstrating the council had notice before your incident. Fixtyer searches for existing reports and attaches them to your claim pack.',
  },
];

export function BedfordCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Bedford Pothole Claims | 6,308 Repairs, U-Roads at 20/100 | Fixtyer"
        description="Bedford Borough Council earns RED DfT ratings with U-roads at 20/100 versus A-roads at 93/100. Pothole fills rose to 6,308 in 2024/25. See what Bedford's two-tier network data means for your Section 58 claim."
        canonical="/councils/bedford"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Bedford', url: '/councils/bedford' },
        ]}
      />
      <FAQSchema faqs={faqs} />
      <ServiceSchema
        councilName="Bedford Borough Council"
        councilSlug="bedford"
        rating="red"
      />
      <SpeakableSchema
        url="/councils/bedford"
        headline="Bedford Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Bedford</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="red" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="red" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            6,308 Potholes Filled in 2024/25 — U-Roads Scored 20/100
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Bedford Borough Council earns a <strong className="text-rose-400">RED</strong> overall DfT rating.
            A-roads score <strong className="text-emerald-400">93.0/100</strong>; unclassified roads score{' '}
            <strong className="text-rose-400">20.0/100</strong>. Pothole fills reached{' '}
            <strong className="text-rose-400">6,308 in 2024/25</strong> — up from 1,343 orders in 2020/21.
            Sixty-one per cent of the network is U-roads surveyed on a two-year CVI cycle, yet the council
            prioritises C-road funding to hold its 3% target. Section 58 still turns on your specific defect.
          </p>

          <div className="bg-rose-500/20 border border-rose-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-rose-400">20/100</div>
            <div className="text-white font-medium mt-2">DfT U-road condition score</div>
            <div className="text-brand-300 text-sm mt-1">
              Versus 93.0/100 on A-roads and 79.5/100 on B/C roads. At the 2025 survey, 18% of U-roads were
              in RED condition — against Bedford&apos;s 2% A-road target and 3% B/C target.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* Official scorecard */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            DfT Scorecard: RED Overall, Two-Tier Condition
          </h2>
          <p className="text-surface-600 mb-8">
            Department for Transport Local Road Maintenance Ratings 2025/26 — verified against Bedford&apos;s June 2025 transparency report
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <h3 className="font-semibold text-brand-900 mb-6">Official scorecard</h3>
              <div className="space-y-3 text-sm">
                {[
                  { label: 'Overall rating', value: 'RED', color: 'text-rose-600' },
                  { label: 'Condition scorecard', value: 'AMBER', color: 'text-amber-600' },
                  { label: 'Spend scorecard', value: 'RED', color: 'text-rose-600' },
                  { label: 'Best practice scorecard', value: 'AMBER', color: 'text-amber-600' },
                  { label: 'A-road condition (DfT score)', value: '93.0/100', color: 'text-emerald-600' },
                  { label: 'B/C road condition (DfT score)', value: '79.5/100', color: 'text-amber-600' },
                  { label: 'U-road condition (DfT score)', value: '20.0/100', color: 'text-rose-600' },
                ].map((item) => (
                  <div key={item.label} className="flex justify-between items-center py-2 border-b border-surface-200">
                    <span className="text-surface-600">{item.label}</span>
                    <span className={`font-mono font-bold ${item.color}`}>{item.value}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6">
              <h3 className="font-semibold text-rose-900 mb-4">The two-tier pattern</h3>
              <div className="space-y-4 text-sm">
                <div>
                  <div className="font-semibold text-emerald-800">Classified roads (39% of network)</div>
                  <p className="text-rose-900/80 mt-1">
                    A-roads: 1.6% RED at 2025 survey (target 2%). B/C roads: 3.8% RED (target 3%).
                    SCANNER surveys cover all A-roads annually; B-roads annually and C-roads over two years.
                  </p>
                </div>
                <div>
                  <div className="font-semibold text-rose-800">Unclassified roads (61% of network)</div>
                  <p className="text-rose-900/80 mt-1">
                    530km of residential and minor roads — 18% in RED condition at 2025 survey. No stated
                    condition target. CVI surveys cover 100% of U-roads over a two-year north/south cycle.
                  </p>
                </div>
              </div>
              <p className="text-rose-800 text-sm mt-4 font-medium">
                PAS 2161 data (2025): 67.8% of U-road length falls in categories requiring some form of intervention
                (categories 2–5 combined: 0.1% + 58.2% + 4.4% + 5.1%).
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Network scale */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            874.1km of Roads — Mostly Unclassified
          </h2>
          <p className="text-surface-600 mb-8">
            Network scale from Bedford Borough Council&apos;s June 2025 transparency report
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-white border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">874.1km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Total carriageway</div>
            </div>
            <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-rose-600">530km</div>
              <div className="text-sm text-rose-800 font-medium mt-1">Unclassified (U) roads</div>
              <div className="text-xs text-rose-700 mt-1">61% of the network</div>
            </div>
            <div className="bg-white border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">257.6km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">B and C roads</div>
            </div>
            <div className="bg-white border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">86.5km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">A roads</div>
              <div className="text-xs text-surface-500 mt-1">Including A6 Paula Radcliffe Way</div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-6">
            <table className="w-full text-sm">
              <caption className="sr-only">Bedford highway asset breakdown</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Asset</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Scale</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Footways</td>
                  <td className="px-6 py-4 text-center font-medium">808km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Other public rights of way</td>
                  <td className="px-6 py-4 text-center font-medium">958km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Cycleways</td>
                  <td className="px-6 py-4 text-center font-medium">29km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Highway bridges</td>
                  <td className="px-6 py-4 text-center font-medium">241</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Lighting columns</td>
                  <td className="px-6 py-4 text-center font-medium">14,772</td>
                </tr>
                <tr>
                  <td className="px-6 py-4">Highway gullies</td>
                  <td className="px-6 py-4 text-center font-medium">22,788</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-surface-600 text-sm mb-6">
            Bedford Borough Council does not maintain trunk roads or motorways — those are National Highways&apos;
            responsibility. The A421 is managed by National Highways; report defects there to 0300 123 5000.
          </p>

          <QuoteCard
            quote="The C road condition indicator is showing signs of deterioration, funding is being prioritised on these roads to ensure that the condition indicator remains at 3% or less."
            source="Bedford Borough Council Local Highways Maintenance Transparency Report (June 2025)"
          />
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Condition Data: Classified Roads vs U-Roads
          </h2>
          <p className="text-surface-600 mb-8">
            SCANNER surveys on A and B/C roads; CVI surveys on U-roads over a two-year cycle — RED = should be considered for maintenance
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
            <p className="text-amber-800 text-sm">
              <strong>Counting caveat:</strong> Bedford publishes U-road condition as RED percentage only — not
              full red/amber/green breakdowns like classified roads. Pre-2022 pothole figures count orders raised,
              not individual potholes; from late 2022 the council records potholes per street or order.
            </p>
          </div>

          {/* A roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A roads (86.5km) — SCANNER annually, target 2% RED</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { year: '2020', red: '1.3%', amber: '10.3%', green: '88.4%' },
                    { year: '2021', red: '1.7%', amber: '9.5%', green: '88.8%' },
                    { year: '2022', red: '1.6%', amber: '11.9%', green: '86.6%' },
                    { year: '2023', red: '1.1%', amber: '13.8%', green: '89.0%' },
                    { year: '2024', red: '1.9%', amber: '15.0%', green: '83.0%' },
                    { year: '2025', red: '1.6%', amber: '12.3%', green: '86.1%', highlight: true },
                  ].map((row) => (
                    <tr key={row.year} className={`border-b border-surface-100 ${row.highlight ? 'bg-emerald-50' : ''}`}>
                      <td className={`px-4 py-2 font-mono ${row.highlight ? 'font-bold' : ''}`}>{row.year}</td>
                      <td className={`px-4 py-2 text-center ${row.highlight ? 'font-bold text-emerald-600' : ''}`}>{row.red}</td>
                      <td className="px-4 py-2 text-center">{row.amber}</td>
                      <td className="px-4 py-2 text-center">{row.green}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              A-road RED condition has stayed at or below Bedford&apos;s 2% target across the published series.
              The council cites additional funding for the A6 Paula Radcliffe Way as benefiting classified roads.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">B and C roads (257.6km) — SCANNER, target 3% RED</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { year: '2020', red: '2.5%', amber: '18.9%', green: '78.5%' },
                    { year: '2021', red: '2.4%', amber: '13.7%', green: '83.7%' },
                    { year: '2022', red: '2.6%', amber: '14.7%', green: '82.5%' },
                    { year: '2023', red: '4.9%', amber: '20.0%', green: '74.2%' },
                    { year: '2024', red: '5.1%', amber: '21.8%', green: '73.0%' },
                    { year: '2025', red: '3.8%', amber: '17.9%', green: '73.0%', highlight: true },
                  ].map((row) => (
                    <tr key={row.year} className={`border-b border-surface-100 ${row.highlight ? 'bg-amber-50' : ''}`}>
                      <td className={`px-4 py-2 font-mono ${row.highlight ? 'font-bold' : ''}`}>{row.year}</td>
                      <td className={`px-4 py-2 text-center ${row.highlight ? 'font-bold text-amber-600' : ''}`}>{row.red}</td>
                      <td className="px-4 py-2 text-center">{row.amber}</td>
                      <td className="px-4 py-2 text-center">{row.green}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              B/C RED condition peaked at 5.1% in 2024 before falling to 3.8% in 2025 — still above the 3% target.
              The council states funding is being prioritised on C-roads to hold the indicator at 3% or less.
            </p>
          </div>

          {/* U roads */}
          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 mb-8">
            <h3 className="font-semibold text-rose-900 mb-4">Unclassified roads (530km) — CVI over two years, no stated RED target</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-rose-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-rose-100 border-b border-rose-200">
                    <th className="px-4 py-2 text-left font-semibold text-rose-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">% in RED condition</th>
                    <th className="px-4 py-2 text-center font-semibold text-surface-600">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { year: '2020', pct: '18%', note: 'Baseline' },
                    { year: '2021', pct: '16.5%', note: 'Improving' },
                    { year: '2022', pct: '24.1%', note: '+46% vs 2021' },
                    { year: '2023', pct: '38%', note: 'Peak in published series', highlight: true },
                    { year: '2024', pct: '16%', note: 'Dropped — cause not stated' },
                    { year: '2025', pct: '18%', note: 'Back to 2020 level' },
                  ].map((row) => (
                    <tr key={row.year} className={`border-b border-rose-100 ${row.highlight ? 'bg-rose-100/50' : ''}`}>
                      <td className={`px-4 py-2 font-mono ${row.highlight ? 'font-bold' : ''}`}>{row.year}</td>
                      <td className={`px-4 py-2 text-center font-mono ${row.highlight ? 'font-bold text-rose-600 text-lg' : ''}`}>{row.pct}</td>
                      <td className="px-4 py-2 text-center text-surface-500 text-xs">{row.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="bg-white rounded-xl overflow-hidden border border-rose-200 mb-4">
              <h4 className="font-semibold text-rose-900 px-4 py-3 border-b border-rose-200 text-sm">
                PAS 2161 condition indicators — U-roads (2025 survey)
              </h4>
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-rose-50 border-b border-rose-200">
                    <th className="px-4 py-2 text-left font-semibold text-rose-900">Category</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-900">% of network</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { cat: '1 — No deterioration', pct: '32.1%', color: 'text-emerald-600' },
                    { cat: '2 — Minor deterioration', pct: '0.1%', color: 'text-surface-600' },
                    { cat: '3 — Moderate deterioration', pct: '58.2%', color: 'text-amber-600' },
                    { cat: '4 — Moderate to severe', pct: '4.4%', color: 'text-rose-500' },
                    { cat: '5 — Severe (reconstruction)', pct: '5.1%', color: 'text-rose-600' },
                  ].map((row) => (
                    <tr key={row.cat} className="border-b border-rose-100">
                      <td className="px-4 py-2">{row.cat}</td>
                      <td className={`px-4 py-2 text-center font-mono font-medium ${row.color}`}>{row.pct}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-rose-800 text-sm font-medium">
              Only 32.1% of U-road length shows no deterioration under PAS 2161. Categories 2–5 total 67.8%
              (requiring some form of intervention per the council&apos;s category definitions). CVI surveys cover
              100% of U-roads annually over a two-year north/south split.
            </p>
          </div>

          <QuoteCard
            quote="The condition of Bedford Borough Council's classified road network has benefited significantly from additional funding over recent years in particular funding to improve the condition of The A6 Paula Radcliffe Way one of the Borough's most heavily trafficked road."
            source="Bedford Borough Council Local Highways Maintenance Transparency Report (June 2025)"
          />
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Rising Pothole Patch Counts
          </h2>
          <p className="text-surface-600 mb-8">
            Estimated potholes filled — from Bedford&apos;s transparency report
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <caption className="sr-only">Bedford estimated potholes filled 2020-2025</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Change vs 2020/21</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Data note</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">1,343</td>
                  <td className="px-6 py-4 text-center text-surface-500">Baseline</td>
                  <td className="px-6 py-4 text-center text-surface-500 text-xs">Orders raised</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">1,509</td>
                  <td className="px-6 py-4 text-center text-amber-600">+12.4%</td>
                  <td className="px-6 py-4 text-center text-surface-500 text-xs">Orders raised</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">3,841</td>
                  <td className="px-6 py-4 text-center text-amber-600">+186.0%</td>
                  <td className="px-6 py-4 text-center text-surface-500 text-xs">Individual count</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">6,032</td>
                  <td className="px-6 py-4 text-center text-rose-600">+349.1%</td>
                  <td className="px-6 py-4 text-center text-surface-500 text-xs">Individual count</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">6,308</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">+369.7%</td>
                  <td className="px-6 py-4 text-center text-surface-500 text-xs">Individual count</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <QuoteCard
              quote="Prevention is better than cure – intervening at the right time will reduce the number of potholes forming and prevent bigger problems later."
              source="Bedford Borough Council Local Highways Maintenance Transparency Report (June 2025)"
            />
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Roughly 17 patches a day</h3>
              <p className="text-surface-600 text-sm">
                6,308 pothole repairs in 2024/25 works out to about 17 patches per day across 874.1km — mostly
                on a network where U-roads sit at 18% RED condition. Reactive volume does not prove your specific
                defect was unavoidable; it shows defects still form across the borough.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold mb-4">
            Following the Money
          </h2>
          <p className="text-brand-200 mb-8 max-w-2xl">
            RED spend scorecard — high preventative ratios in recent years, but projected 2025/26 capital below DfT allocation
          </p>

          <div className="bg-brand-900/50 rounded-2xl border border-brand-800 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <caption className="sr-only">Bedford highway maintenance spending 2020-2026</caption>
              <thead>
                <tr className="bg-brand-800 border-b border-brand-700">
                  <th className="px-4 py-3 text-left font-semibold text-brand-300">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">DfT capital (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Capital spend (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Revenue (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Reactive</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-800">
                <tr className="bg-rose-900/30">
                  <td className="px-4 py-3 font-medium">2025/26 (proj.)</td>
                  <td className="px-4 py-3 text-center">6,318</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-400">3,210</td>
                  <td className="px-4 py-3 text-center">1,962</td>
                  <td className="px-4 py-3 text-center">62%</td>
                  <td className="px-4 py-3 text-center">38%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2024/25</td>
                  <td className="px-4 py-3 text-center">4,890</td>
                  <td className="px-4 py-3 text-center text-emerald-400">4,464</td>
                  <td className="px-4 py-3 text-center">1,984</td>
                  <td className="px-4 py-3 text-center text-emerald-400">70%</td>
                  <td className="px-4 py-3 text-center">30%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2023/24</td>
                  <td className="px-4 py-3 text-center">2,199</td>
                  <td className="px-4 py-3 text-center text-emerald-400">3,845</td>
                  <td className="px-4 py-3 text-center">1,820</td>
                  <td className="px-4 py-3 text-center">68%</td>
                  <td className="px-4 py-3 text-center">32%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2022/23</td>
                  <td className="px-4 py-3 text-center">2,433</td>
                  <td className="px-4 py-3 text-center text-emerald-400">4,590</td>
                  <td className="px-4 py-3 text-center">2,330</td>
                  <td className="px-4 py-3 text-center">66%</td>
                  <td className="px-4 py-3 text-center">34%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2021/22</td>
                  <td className="px-4 py-3 text-center">3,244</td>
                  <td className="px-4 py-3 text-center text-emerald-400">3,598</td>
                  <td className="px-4 py-3 text-center">3,087</td>
                  <td className="px-4 py-3 text-center">49%</td>
                  <td className="px-4 py-3 text-center">51%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2020/21</td>
                  <td className="px-4 py-3 text-center">3,244</td>
                  <td className="px-4 py-3 text-center text-emerald-400">4,193</td>
                  <td className="px-4 py-3 text-center">3,087</td>
                  <td className="px-4 py-3 text-center">57%</td>
                  <td className="px-4 py-3 text-center">42%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-emerald-500/20 rounded-xl p-6 border border-emerald-500/30">
              <h3 className="font-semibold text-white mb-3">What the council does well on paper</h3>
              <p className="text-brand-200 text-sm">
                Capital spend exceeded DfT allocation in five of the six published years. Preventative maintenance
                share reached 70% in 2024/25. The council states a 10-year resurfacing lifecycle and uses risk-based
                criteria — condition surveys, bus routes, primary routes and Highways Helpdesk reports — to prioritise schemes.
              </p>
            </div>
            <div className="bg-rose-500/20 rounded-xl p-6 border border-rose-500/30">
              <h3 className="font-semibold text-white mb-3">Why spend is still RED</h3>
              <p className="text-brand-200 text-sm">
                Projected 2025/26 capital spend (£3.21m) is roughly half the £6.32m DfT allocation. U-road DfT
                condition remains 20/100 despite 62–70% preventative spend in recent years. Pothole fills tripled
                from 2021/22 to 2024/25 on comparable counting. The DfT spend scorecard reflects outcomes, not just budgets.
              </p>
            </div>
          </div>

          <QuoteCard
            quote="Our current lifecycle for resurfacing work is 10 years."
            source="Bedford Borough Council Local Highways Maintenance Transparency Report (June 2025)"
          />
        </div>
      </section>

      {/* 2025/26 plans */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Planned Work 2025/26
          </h2>
          <p className="text-surface-600 mb-8">
            What Bedford says it will deliver this financial year — against an 874.1km network
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">3.47 mi</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Resurfacing (20 schemes)</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">3.37 mi</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Patching / surface dressing</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-amber-600">25 streets</div>
              <div className="text-sm text-amber-800 font-medium mt-1">Pothole / patching programme</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">11.32 km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Resurfaced in 2024/25</div>
            </div>
          </div>

          <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
            <h3 className="font-semibold text-brand-900 mb-3">Coverage maths</h3>
            <p className="text-surface-600 text-sm">
              Bedford completed 11.324km of carriageway resurfacing in 2024/25 — roughly 1.3% of the 874.1km network.
              The 2025/26 plan adds approximately 3.47 miles (~5.6km) of resurfacing plus 25 streets for pothole or
              patching work. The remaining network relies on reactive repairs, cyclical inspections and the two-year
              U-road CVI survey cycle — on roads where 18% were in RED condition at the last survey.
            </p>
          </div>
        </div>
      </section>

      {/* Inspections and Section 58 */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Inspections, Surveys and Section 58
          </h2>
          <p className="text-surface-600 mb-8">
            How Bedford says it knows the condition of its network — and where gaps appear
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Survey frequency</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• <strong>A roads:</strong> SCANNER surveys 100% in both directions every year</li>
                <li>• <strong>B roads:</strong> SCANNER annually in both directions</li>
                <li>• <strong>C roads:</strong> SCANNER over two years, north and south halves</li>
                <li>• <strong>U roads:</strong> CVI surveys 100% annually over two years, north and south halves</li>
                <li>• <strong>Defects:</strong> cyclical inspections plus Highways Helpdesk reports</li>
              </ul>
            </div>
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Maintenance approach</h3>
              <p className="text-surface-600 text-sm mb-3">
                Bedford undertakes patching via a minor works contractor on all road classes up to a maximum of
                10% of the road. Resurfacing follows a stated 10-year lifecycle. The council recently purchased
                Road Mender Elastomac for hot-applied rubber-modified asphalt repairs.
              </p>
              <p className="text-surface-600 text-sm">
                Preventative programmes use condition survey data, bus routes, primary routes and Helpdesk reporting
                to prioritise schemes — criteria that favour classified and primary corridors over most U-roads.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8 mb-8">
            <h3 className="font-display text-xl font-bold mb-4">Section 41 vs Section 58</h3>
            <p className="text-brand-200 mb-4">
              Under <strong className="text-white">Section 41 of the Highways Act 1980</strong>, Bedford must
              maintain public highways. To defend a claim under <strong className="text-white">Section 58</strong>,
              it must show a reasonable system for inspecting and repairing the specific defect — not just publish
              strong A-road SCANNER scores.
            </p>
            <ul className="text-brand-300 space-y-2 text-sm">
              <li>• Was your U-road on the north or south half of the two-year CVI cycle — and had it been surveyed recently?</li>
              <li>• Did the defect meet intervention criteria during cyclical safety inspections?</li>
              <li>• Were there prior reports via the Highways Helpdesk or FixMyStreet giving actual notice?</li>
              <li>• Does photographic evidence show defect age beyond the survey or inspection interval?</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Balanced claims section */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a RED-Rated Borough
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Bedford spends heavily on classified roads — but U-road evidence is structurally weaker
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the council&apos;s favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A-road condition 93.0/100 — 1.6% RED at 2025 survey
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Capital spend above DfT allocation in most recent years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 62–70% preventative spend in 2022/23–2024/25
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented asset management aligned to Well-Managed Highway Infrastructure code
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Additional A6 funding cited for classified network improvement
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a prepared Section 58 defence on A-roads and well-maintained classified corridors.
              </p>
            </div>

            <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6">
              <h3 className="font-semibold text-rose-900 mb-4">What works in yours</h3>
              <ul className="text-rose-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> RED overall and spend scorecards from DfT
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> U-road DfT score 20.0/100 vs A-road 93.0/100
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 530km of U-roads (61%) — 18% RED at 2025 survey, no stated target
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 6,308 pothole fills in 2024/25 — reactive workload still rising
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council prioritises C-road funding; U-roads lack equivalent targets
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> PAS 2161: 67.8% of U-road length in categories needing intervention
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a borough with strong A-road scores and above-allocation spending on classified roads, your
              claim lives or dies on the <strong className="text-white">specific defect and road class</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole — proof of actual notice beyond network surveys</li>
              <li>• Photos showing defect size, depth and age (weathered edges, previous patching)</li>
              <li>• Road class — on a U-road, the two-year CVI cycle and 20/100 DfT score are your strongest structural arguments</li>
              <li>• Council quotes on C-road prioritisation versus U-roads with no stated target</li>
            </ul>
            <p className="text-white font-medium">
              Fixtyer builds exactly this case: prior-report search, photo assessment, and citations from Bedford&apos;s
              own transparency data where it helps you — without overstating what RED ratings guarantee.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-12 bg-surface-100 border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-3">
            Report a Pothole to Bedford Borough Council
          </h2>
          <p className="text-surface-600 mb-6">
            Reporting a defect creates a record the council had notice. Do this before claiming — and tell us
            when you reported it so we can reference it in your pack. The A421 is managed by National Highways.
          </p>
          <a
            href="https://www.bedford.gov.uk/parking-roads-and-travel/roads-and-pavements/potholes"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-6 py-3 bg-brand-600 text-white font-semibold rounded-xl hover:bg-brand-700 transition-colors"
          >
            Report a pothole — bedford.gov.uk
            <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Bedford?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A two-tier network demands a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road 20/100 scorecard argument</li>
                <li>• No prior-report search</li>
                <li>• No two-year CVI survey-gap analysis</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ U-road 20/100 vs A-road 93/100 documented</li>
                <li>✅ Two-year CVI survey cycle argued</li>
                <li>✅ 6,308 pothole repairs in 2024/25 cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Bedford</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=bedford"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Bedford Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            {faqs.map((faq) => (
              <div key={faq.question} className="bg-white rounded-xl p-6 border border-surface-200">
                <h3 className="font-semibold text-brand-900 mb-2">{faq.question}</h3>
                <p className="text-surface-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.bedford.gov.uk/parking-roads-and-travel/local-highways-maintenance-transparency-report"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Bedford Borough Council Local Highways Maintenance Transparency Report (June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
